// My renewals and All renewals (spec C7.3, C4, C5): one list design for every role. Markup from the
// Table, SegmentedTabs, StatCard, AlertBar, Chip, Menu, Pill and Tag cards and the Renewals board.
// Search, Filters, sort, Group by, View and the team strip work on the rows already on the page and
// live in the address. Actions whose feature is not built yet are disabled with the reason
// (shell/placeholders.ts).
import { branchTag } from '../renewals/branch-lines';
import { Fragment, useEffect, useState } from 'react';
import {
  Link,
  useLoaderData,
  useLocation,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from 'react-router';
import type { Api, IncreaseRule, RenewalRow, RenewalsView } from '../auth/api';
import { useUser } from '../auth/user';
import { fetchingTip } from '../renewals/fetch-lines';
import { managesDepartment, renewalsSubtitle, type Role } from '../roles';
import {
  BulkBar,
  ClosedRangeChip,
  CollapseChip,
  FiltersButton,
  GroupByChip,
  RowMenu,
  SortHeader,
  TeamStrip,
  ViewChip,
} from '../renewals/ListControls';
import {
  appliedChips,
  applyFilters,
  cleared,
  comparator,
  firstDir,
  groupList,
  groupModes,
  hasNarrowing,
  isStepKey,
  stepCounts,
  noSearchMatch,
  readState,
  sortDefaultOf,
  stateParams,
  teamOf,
  viewOf,
  viewState,
  type ListState,
  type SortKey,
} from '../renewals/list-view';
import { RenewalQuickView } from '../renewals/RenewalQuickView';
import {
  closedRangeOf,
  closedRangeParams,
  inClosedRange,
  noClosedIn,
} from '../renewals/closed-lines';
import { recheckTag } from '../renewals/step-lines';
import { useCollapsed } from '../renewals/use-collapsed';
import { useSyncNow } from '../renewals/use-sync-now';
import { useAddNote } from '../renewals/use-notes';
import { AwaitingTag } from '../renewals/HandToAe';
import { useHandToAe } from '../renewals/use-hand-to-ae';
import { useHandoffAnswers } from '../renewals/use-handoff-answers';
import { answerRow, canTakeOver, passRow, withMeRow } from '../renewals/ae-handoff-lines';
import {
  actByChip,
  followUpText,
  fromName,
  resultText,
  rowReason,
  sentText,
} from '../renewals/ae-handoff-lines';
import { useReassign } from '../renewals/use-reassign';
import type { Reassigned } from '../renewals/reassign-lines';
import { personKey, readJson, writeJson } from '../shell/browser-store';
import { PlaceholderButton } from '../shell/Placeholder';
import { Toast } from '../shell/Toast';
import {
  attentionLine,
  overdueLine,
  band,
  change,
  daysLabel,
  daysPill,
  emptyPage,
  emptyTab,
  expiresTip,
  increaseTooltip,
  inStat,
  inTab,
  money,
  renewalCount,
  renewedTip,
  shortLine,
  showIncreaseTag,
  showing,
  stageOf,
  statClass,
  statsFor,
  statuses,
  syncFailedLine,
  syncLine,
  tabsFor,
  defaultTab,
  isAeTab,
  isOverdue,
  nyToday,
  when,
  type Page,
  type StatKey,
  type TabKey,
} from '../renewals/renewal-lines';
import { EmptyState } from '../shell/EmptyState';
import { PageHeader } from '../shell/PageHeader';
import { Tip } from '../shell/Tip';
import {
  AlertTriangleIcon,
  CheckIcon,
  ChevronDownIcon,
  DownloadIcon,
  NoteIcon,
  RefreshIcon,
  XIcon,
} from '../shell/icons';
import { useAutoRefresh } from '../shell/auto-refresh';

export type RenewalsLoaderData = RenewalsView;

/** Handled by shows for the Manager, the Admin and the AE (spec C7.3). */
const showsHandledBy = (role: Role) => role !== 'Renewal VA';

/**
 * Row checkboxes and the bulk bar: VA and Manager (and Admin) on the Open and Waiting tabs, the AE on
 * Needs your answer (bulk Accept and Pass, spec C7.3) and With me (bulk Pass, S15 batch 7).
 */
const selectsRows = (role: Role, tab: TabKey | null) =>
  role === 'Account Executive'
    ? tab === 'ask' || tab === 'withme'
    : tab === 'open' || tab === 'waiting';

type Column = { label: string; sort: SortKey; width: string };

// Insured, [Handled by], Policy #, Line, Expires, Prior, Renewal, Change, Status. Pills, money and
// Change never shrink below their content; Insured shrinks first, then Policy #.
const columnsFor = (handledBy: boolean): Column[] => {
  const all: (Column | null)[] = [
    { label: 'Insured', sort: 'insured', width: 'minmax(0, 1.6fr)' },
    handledBy ? { label: 'Handled by', sort: 'handledBy', width: 'minmax(0, 1fr)' } : null,
    { label: 'Policy #', sort: 'policy', width: 'minmax(0, 1.1fr)' },
    { label: 'Line', sort: 'line', width: 'minmax(0, 0.6fr)' },
    { label: 'Expires', sort: 'days', width: 'minmax(max-content, 0.5fr)' },
    { label: 'Prior', sort: 'prior', width: 'minmax(max-content, 0.7fr)' },
    { label: 'Renewal', sort: 'renewal', width: 'minmax(max-content, 0.7fr)' },
    { label: 'Change', sort: 'change', width: 'minmax(max-content, 0.6fr)' },
    { label: 'Status', sort: 'status', width: 'minmax(max-content, 1.5fr)' },
  ];
  return all.filter((c): c is Column => c !== null);
};

// The AE's three tabs (the Renewals board's hand_ask, hand_with and hand_done columns). Pills, tags
// and dates are never cut; Insured shrinks first.
const aeLead: Column[] = [
  { label: 'Insured', sort: 'insured', width: 'minmax(0, 1.3fr)' },
  { label: 'Reason', sort: 'reason', width: 'minmax(max-content, 1fr)' },
  { label: 'Policy #', sort: 'policy', width: 'minmax(0, 1.1fr)' },
  { label: 'Line', sort: 'line', width: 'minmax(0, 0.6fr)' },
  { label: 'Expires', sort: 'days', width: 'minmax(max-content, 0.5fr)' },
];
const aeColumns: Record<'ask' | 'withme' | 'finished', Column[]> = {
  ask: [
    ...aeLead,
    { label: 'Act by', sort: 'actBy', width: 'minmax(max-content, 0.9fr)' },
    { label: 'From', sort: 'from', width: 'minmax(0, 0.9fr)' },
    { label: 'Sent', sort: 'sent', width: 'minmax(max-content, 0.9fr)' },
  ],
  withme: [
    ...aeLead,
    { label: 'Act by or Follow up', sort: 'actBy', width: 'minmax(max-content, 1.2fr)' },
    { label: 'Status', sort: 'status', width: 'minmax(max-content, 1.4fr)' },
  ],
  finished: [
    ...aeLead,
    { label: 'Result', sort: 'result', width: 'minmax(0, 2.2fr)' },
    { label: 'From', sort: 'from', width: 'minmax(0, 0.9fr)' },
  ],
};

/** The AE's My renewals cards and the tab whose columns their rows show. */
const aeStatTab = {
  waitingOnMe: 'ask',
  overdueOnMe: 'ask',
  followUps: 'withme',
  finishedWeek: 'finished',
} as const;

const isTrue = (v: unknown): v is boolean => v === true;

export function RenewalsPage({ page, api }: { page: Page; api: Api }) {
  const { renewals, sync, increaseRule } = useLoaderData() as RenewalsLoaderData;
  const user = useUser();
  const { role } = user;
  const now = new Date();
  const tabs = tabsFor(page, role);
  const stats = statsFor(page, role);
  const handledBy = showsHandledBy(role);
  const manager = managesDepartment(role);

  // The tab, the stat card, the search, the filters, the sort and the grouping live in the address,
  // so Back from the Record returns to the list as it was left (spec C7.4).
  const [params, setParams] = useSearchParams();
  const firstTab = defaultTab(page, role);
  const tab = tabs.find((t) => t.key === params.get('tab'))?.key ?? firstTab;
  // The AE's handoff tabs open sorted by Act by (spec C7.3), Finished newest first.
  const sortDefault = sortDefaultOf(tab);
  const list = readState(params, role, sortDefault);
  const searching = list.q.trim() !== '';
  // A search looks across every stat card, so one left in the address is ignored while it is on.
  const stat = searching ? null : (stats.find((s) => s.key === params.get('stat'))?.key ?? null);
  const update = (changes: Record<string, string | string[] | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          next.delete(k);
          if (Array.isArray(v)) v.forEach((x) => next.append(k, x));
          else if (v !== null) next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  const setList = (s: ListState) => update(stateParams(s, sortDefault));
  const patchList = (changes: Partial<ListState>) => setList({ ...list, ...changes });
  // The Outcome filter belongs to the Closed tab: leaving it clears the filter.
  const setTab = (t: TabKey) =>
    update({ tab: t === firstTab ? null : t, stat: null, q: null, step: null, outcome: null });
  // The Closed tab's "Closed between" (S16): the server sent the renewals closed in it.
  const closedRange = closedRangeOf(params, nyToday(new Date()));
  const tabRows = (key: TabKey) =>
    renewals.filter(
      (r) => inTab(r, page, key, user.id) && (key !== 'closed' || inClosedRange(r, closedRange)),
    );
  const stepParam = params.get('step');
  // Like a tab, a stat card clears the search.
  const setStat = (s: StatKey | null) => update({ stat: s, q: null });

  const collapsed = useCollapsed(user.id, page);
  // The red bar can be dismissed for this session (AlertBar card), per person and page.
  const barKey = personKey('attention-bar-hidden', user.id, page);
  const [barHidden, setBarHidden] = useState(() => readJson('session', barKey, false, isTrue));
  const hideBar = () => {
    setBarHidden(true);
    writeJson('session', barKey, true);
  };

  // The open row by id, so a reloaded list never shows an old copy in the Quick view.
  const [openId, setOpenId] = useState<string | null>(null);
  const open = renewals.find((r) => r.id === openId) ?? null;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const inThisTab = tabRows(tab);
  // The step sub-filter under Open (spec C4): the VA and the Manager, not with a search or stat card.
  const steps =
    tab === 'open' && !stat && !searching && role !== 'Account Executive'
      ? stepCounts(inThisTab)
      : [];
  const step = steps.length > 0 && isStepKey(stepParam) ? stepParam : null;
  const inThisStep = step ? inThisTab.filter((r) => r.currentStep === step) : inThisTab;
  // A search and a stat card look across every tab, so their counts and rows always match.
  const base = searching
    ? renewals
    : stat
      ? renewals.filter((r) => inStat(r, stat, increaseRule, now, user.id))
      : inThisStep;
  const rows = applyFilters(base, list, increaseRule, now);
  // The AE's Finished rows (the tab or its card) describe and sort by the handoff that ended.
  const finishedRows =
    page === 'my' &&
    role === 'Account Executive' &&
    !searching &&
    (stat ? stat === 'finishedWeek' : tab === 'finished');
  // With me: the rows to follow up today come to the top that day (spec C6).
  const withMeRows =
    page === 'my' && role === 'Account Executive' && !searching && !stat && tab === 'withme';
  const groups = groupList(
    rows,
    list.group,
    comparator(list.sort, list.dir, finishedRows),
    withMeRows ? nyToday(now) : null,
  );
  const grouped = list.group !== 'none';
  const allClosed = grouped && groups.length > 0 && groups.every((g) => collapsed.closed(g.key));
  // On the AE's My renewals the overdue-handoff bar takes the place of the needs-attention bar (spec
  // C5, C6; S15 batch 8): the handoffs waiting on them past their Act by date.
  const aeMine = page === 'my' && role === 'Account Executive';
  const attention = aeMine ? 0 : renewals.filter((r) => inStat(r, 'attention')).length;
  const overdue = aeMine
    ? renewals.filter((r) => inStat(r, 'overdueOnMe', increaseRule, now, user.id)).length
    : 0;
  const failed = syncFailedLine(sync, now);
  // Sync now and Export: the Manager and the Admin, on All renewals (spec C7.3).
  const onAll = page === 'all' && manager;
  const syncNow = useSyncNow(api, sync);
  // Reassign (spec C6): after a change the list is read again; a toast handed over from the Record
  // (a VA who moved their own record away lands here) shows once.
  const revalidator = useRevalidator();
  const location = useLocation();
  const navigate = useNavigate();
  const [handedOver] = useState(
    () => (location.state as { reassigned?: Reassigned } | null)?.reassigned ?? null,
  );
  useEffect(() => {
    if (handedOver) {
      void navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const reassign = useReassign(
    api,
    { id: user.id, name: user.name },
    () => {
      setSelected(new Set());
      void revalidator.revalidate();
    },
    handedOver,
  );
  // Hand to AE (row menu, Quick view): the renewal stays on the list with an Awaiting AE tag.
  const hand = useHandToAe(api, () => void revalidator.revalidate());
  // Add note (row menu, Quick view): the popup, the toast with Undo, then the list is read again for
  // the note icon and the Quick view reads its notes again.
  const [notesVersion, setNotesVersion] = useState(0);
  // Auto refresh (issue 291): the list is read again on its own, and the open Quick view's notes with it,
  // so an answer from the AE or a new handoff shows without pressing refresh.
  useAutoRefresh(() => setNotesVersion((v) => v + 1));
  const addNote = useAddNote(api, () => {
    setNotesVersion((v) => v + 1);
    void revalidator.revalidate();
  });
  // The AE's Accept and Send back (spec C6): toasts with Undo, then the list is read again.
  const answers = useHandoffAnswers(
    api,
    () => {
      setSelected(new Set());
      void revalidator.revalidate();
    },
    user.id,
  );
  const tabForRows = stat || searching ? null : tab;
  const canSelect = selectsRows(role, tabForRows);
  const shownIds = rows.map((r) => r.id);
  const picked = shownIds.filter((id) => selected.has(id));
  const allPicked = shownIds.length > 0 && picked.length === shownIds.length;
  const narrowed = hasNarrowing(list) || stat !== null;
  const people = new Map(
    renewals.filter((r) => r.handledBy).map((r) => [r.handledBy!.id, r.handledBy!.name]),
  );
  const chips = appliedChips(list, (id) => people.get(id) ?? 'Someone');
  // The AE's tabs have their own columns; on My renewals a card shows its tab's (the board's cards
  // open their tab).
  const aeKind = isAeTab(tabForRows)
    ? tabForRows
    : page === 'my' && role === 'Account Executive' && stat
      ? (aeStatTab[stat as keyof typeof aeStatTab] ?? null)
      : null;
  const columns = aeKind ? aeColumns[aeKind] : columnsFor(handledBy);
  const grid = [canSelect ? 'min-content' : null, ...columns.map((c) => c.width), 'max-content']
    .filter(Boolean)
    .join(' ');

  const clearAll = () => update({ ...stateParams(cleared(list), sortDefault), stat: null });
  const sortBy = (key: SortKey) =>
    patchList({
      sort: key,
      dir: list.sort === key ? (list.dir === 'asc' ? 'desc' : 'asc') : firstDir(key),
    });
  const pick = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <PageHeader
        title={page === 'my' ? 'My renewals' : 'All renewals'}
        subtitle={renewalsSubtitle(page, role)}
        actions={
          <>
            {page === 'all' && role === 'Account Executive' && (
              <PlaceholderButton id="list.scope" className="btn">
                <span className="muted">Showing:</span>
                <span className="ink">Whole department</span>
                <ChevronDownIcon className="ico s12 muted" />
              </PlaceholderButton>
            )}
            {onAll && (
              <button
                type="button"
                className="btn"
                disabled={syncNow.syncing}
                onClick={syncNow.start}
              >
                <RefreshIcon />
                Sync now
              </button>
            )}
            {onAll && (
              <PlaceholderButton id="list.export" className="btn">
                <DownloadIcon />
                Export
              </PlaceholderButton>
            )}
          </>
        }
      />

      {failed && !syncNow.syncing && (
        <div className="abar" role="alert">
          <AlertTriangleIcon />
          <span className="t13 w5">{failed}</span>
          {onAll && (
            <button type="button" className="abar-btn" onClick={syncNow.start}>
              Try again
            </button>
          )}
        </div>
      )}
      {(attention > 0 || overdue > 0) && !barHidden && (
        <div className="abar" role="status">
          <AlertTriangleIcon />
          <span className="t13 w5">{aeMine ? overdueLine(overdue) : attentionLine(attention)}</span>
          <button
            type="button"
            className="abar-btn"
            onClick={() => setStat(aeMine ? 'overdueOnMe' : 'attention')}
          >
            {aeMine ? 'Show overdue' : 'Show them'}
          </button>
          <button
            type="button"
            className="abar-x"
            aria-label="Dismiss for this session"
            onClick={hideBar}
          >
            <XIcon />
          </button>
        </div>
      )}

      <section className="stats" aria-label="Summary">
        {stats.map((s) => {
          const n = renewals.filter((r) => inStat(r, s.key, increaseRule, now, user.id)).length;
          const on = stat === s.key;
          return (
            <button
              key={s.key}
              type="button"
              className={on ? 'card tstat on' : 'card tstat'}
              aria-pressed={on}
              onClick={() => setStat(on ? null : s.key)}
            >
              <span className="t12 text2 w5">{s.label}</span>
              <span className={statClass(s.key, n)}>{n}</span>
              {s.sub && <span className="t11 muted">{s.sub}</span>}
            </button>
          );
        })}
      </section>

      {onAll && (
        <TeamStrip
          team={teamOf(renewals)}
          person={list.person}
          onPick={(person) => patchList({ person })}
        />
      )}

      {renewals.length === 0 ? (
        <EmptyState text={emptyPage(role)} />
      ) : (
        <div className="col g8">
          <div className="ltb">
            <div className="row g12 wrap">
              <div className="seg" role="group" aria-label="Status">
                {tabs.map((t) => {
                  const on = !stat && !searching && tab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      className={on ? 'seg-b on' : 'seg-b'}
                      aria-pressed={on}
                      onClick={() => setTab(t.key)}
                    >
                      {t.label}
                      <span className="n">{tabRows(t.key).length}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="ltb-r">
              <GroupByChip
                value={list.group}
                options={groupModes(role)}
                onChange={(group) => patchList({ group })}
              />
              {grouped && groups.length > 0 && (
                <CollapseChip allClosed={allClosed} onClick={() => collapsed.setAll(!allClosed)} />
              )}
              <ViewChip value={viewOf(list)} onPick={(v) => setList(viewState(list, v))} />
              <FiltersButton
                state={list}
                rows={base}
                role={role}
                tab={tabForRows}
                rule={increaseRule}
                onApply={setList}
              />
              {narrowed && (
                <button type="button" className="linkbtn tb-clear" onClick={clearAll}>
                  Clear all
                </button>
              )}
            </div>
          </div>
          {tab === 'closed' && !stat && !searching && (
            <div className="row g8 wrap">
              {/* No key: the popover stays open while the dates are typed (Codex on PR 270). */}
              <ClosedRangeChip range={closedRange} onChange={(r) => update(closedRangeParams(r))} />
            </div>
          )}
          {steps.length > 0 && (
            <div className="ltb">
              <div className="sub" role="group" aria-label="Step">
                <button
                  type="button"
                  className={step === null ? 'sub-b on' : 'sub-b'}
                  aria-pressed={step === null}
                  onClick={() => update({ step: null })}
                >
                  All {inThisTab.length}
                </button>
                {steps.map((s) => (
                  <Fragment key={s.step}>
                    <span className="sub-dot" aria-hidden="true">
                      ·
                    </span>
                    <button
                      type="button"
                      className={step === s.step ? 'sub-b on' : 'sub-b'}
                      aria-pressed={step === s.step}
                      onClick={() => update({ step: s.step })}
                    >
                      {s.label} {s.n}
                    </button>
                  </Fragment>
                ))}
              </div>
            </div>
          )}
          {(stat || chips.length > 0 || searching) && (
            <div className="row g6 wrap" aria-label="Applied filters">
              {searching && (
                <button
                  type="button"
                  className="chip chip-sm"
                  aria-label="Clear search"
                  onClick={() => patchList({ q: '' })}
                >
                  Search &quot;{list.q.trim()}&quot;
                  <XIcon className="ico x" />
                </button>
              )}
              {stat && (
                <button
                  type="button"
                  className="chip chip-sm"
                  aria-label={`Remove filter ${stats.find((s) => s.key === stat)?.label}`}
                  onClick={() => setStat(null)}
                >
                  {stats.find((s) => s.key === stat)?.label}
                  <XIcon className="ico x" />
                </button>
              )}
              {chips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className="chip chip-sm"
                  aria-label={`Remove filter ${c.label}`}
                  onClick={() => patchList(c.without)}
                >
                  {c.label}
                  <XIcon className="ico x" />
                </button>
              ))}
            </div>
          )}

          <section className="col g8" aria-label="Renewals">
            <div className="hdr" style={{ gridTemplateColumns: grid }}>
              {canSelect && (
                <div className="th selc">
                  <input
                    type="checkbox"
                    className="cbx"
                    aria-label="Select all shown renewals"
                    checked={allPicked}
                    onChange={() => setSelected(allPicked ? new Set() : new Set(shownIds))}
                  />
                </div>
              )}
              {columns.map((c) => (
                <SortHeader
                  key={c.label}
                  label={c.label}
                  active={list.sort === c.sort}
                  dir={list.dir}
                  onSort={() => sortBy(c.sort)}
                />
              ))}
              <div className="th" aria-hidden="true" />
            </div>
            {rows.length === 0 ? (
              searching ? (
                <EmptyState
                  text={noSearchMatch(list.q)}
                  action={{ label: 'Clear search', onClick: () => patchList({ q: '' }) }}
                />
              ) : narrowed ? (
                <EmptyState
                  text="No renewals match these filters."
                  action={{ label: 'Clear all', onClick: clearAll }}
                />
              ) : (
                <EmptyState text={tab === 'closed' ? noClosedIn(closedRange) : emptyTab(tab)} />
              )
            ) : (
              groups.map((g) => {
                const isClosed = grouped && collapsed.closed(g.key);
                return (
                  <div key={g.key} className={g.attention ? 'gcard urg' : 'gcard'}>
                    {grouped && (
                      <button
                        type="button"
                        className={['ghead', g.attention ? 'urg' : '', isClosed ? 'closed' : '']
                          .join(' ')
                          .trim()}
                        aria-expanded={!isClosed}
                        onClick={() => collapsed.toggle(g.key)}
                      >
                        <ChevronDownIcon
                          className={isClosed ? 'ico s14 chev closed' : 'ico s14 chev'}
                        />
                        <span className="glabel">{g.label}</span>
                        <span className="tag gcount">{renewalCount(g.rows.length)}</span>
                        <span className="ml-auto t12 muted">
                          {g.attention ? 'Hover the days for the reason' : g.meta}
                        </span>
                      </button>
                    )}
                    {!isClosed && (
                      <div>
                        {g.rows.map((r) => (
                          <Row
                            key={r.id}
                            row={r}
                            grid={grid}
                            handledBy={handledBy}
                            aeKind={aeKind}
                            now={now}
                            rule={increaseRule}
                            role={role}
                            page={page}
                            tab={tabForRows}
                            open={open?.id === r.id}
                            checkbox={
                              canSelect ? { on: selected.has(r.id), pick: () => pick(r.id) } : null
                            }
                            onOpen={() => setOpenId(r.id)}
                            onReassign={() => reassign.open([r])}
                            onHand={() => hand.open(r)}
                            onAddNote={() => addNote.open(r)}
                            // On Needs your answer only (the board); the Quick view offers them anywhere.
                            onAccept={
                              aeKind === 'ask' && answerRow(r, user.id)
                                ? () => answers.accept(answerRow(r, user.id)!)
                                : null
                            }
                            onSendBack={
                              aeKind === 'ask' && answerRow(r, user.id)
                                ? () => answers.sendBack(answerRow(r, user.id)!)
                                : null
                            }
                            onPass={
                              passRow(r, user.id)
                                ? () => answers.pass([passRow(r, user.id)!])
                                : null
                            }
                            onTakeOver={
                              canTakeOver(r, role, user.id) ? () => answers.takeOver([r]) : null
                            }
                            meId={user.id}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </section>
          <div className="tfoot">
            <span>
              {showing(rows.length, stat || searching ? renewals.length : inThisTab.length)}
            </span>
            <span>
              {syncNow.syncing ? 'Syncing from AMS360 now. Read only.' : syncLine(sync, now)}
            </span>
          </div>
        </div>
      )}

      {canSelect &&
        (role === 'Account Executive' ? (
          <BulkBar
            count={picked.length}
            manager={false}
            onClear={() => setSelected(new Set())}
            onAccept={
              tabForRows === 'ask'
                ? () =>
                    answers.acceptMany(
                      rows
                        .filter((r) => selected.has(r.id))
                        .flatMap((r) => answerRow(r, user.id) ?? []),
                    )
                : undefined
            }
            onPass={() =>
              answers.pass(
                rows.filter((r) => selected.has(r.id)).flatMap((r) => passRow(r, user.id) ?? []),
              )
            }
          />
        ) : (
          <BulkBar
            count={picked.length}
            manager={manager}
            onClear={() => setSelected(new Set())}
            onReassign={() => reassign.open(rows.filter((r) => selected.has(r.id)))}
          />
        ))}
      {syncNow.toast && <Toast key={syncNow.toast.id} text={syncNow.toast.text} />}
      {reassign.element}
      {hand.element}
      {addNote.element}
      {answers.element}
      {open && (
        <RenewalQuickView
          api={api}
          role={role}
          meId={user.id}
          row={open}
          onReassign={() => reassign.open([open])}
          onHand={() => hand.open(open)}
          onAddNote={() => addNote.open(open)}
          onAccept={answerRow(open, user.id) && (() => answers.accept(answerRow(open, user.id)!))}
          onSendBack={
            answerRow(open, user.id) && (() => answers.sendBack(answerRow(open, user.id)!))
          }
          onFollowUp={answers.followUp}
          onFinish={
            withMeRow(open, user.id) &&
            ((steps) => answers.finish(withMeRow(open, user.id)!, steps, user.initials))
          }
          onPass={passRow(open, user.id) && (() => answers.pass([passRow(open, user.id)!]))}
          onTakeOver={canTakeOver(open, role, user.id) ? () => answers.takeOver([open]) : null}
          notesVersion={notesVersion}
          page={page}
          tab={tabForRows}
          now={now}
          rule={increaseRule}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

type RowProps = {
  row: RenewalRow;
  grid: string;
  handledBy: boolean;
  /** One of the AE's tabs, with its own columns; null for every other list. */
  aeKind: 'ask' | 'withme' | 'finished' | null;
  now: Date;
  rule: IncreaseRule;
  role: Role;
  page: Page;
  tab: TabKey | null;
  open: boolean;
  checkbox: { on: boolean; pick: () => void } | null;
  onOpen: () => void;
  onReassign: () => void;
  onHand: () => void;
  onAddNote: () => void;
  /** Accept and Send back, while the handoff waits on this AE; null otherwise. */
  onAccept: (() => void) | null;
  onSendBack: (() => void) | null;
  /** Pass to another AE, a handoff on this AE; Take over, the AE's own move (S15 batch 7). */
  onPass: (() => void) | null;
  onTakeOver: (() => void) | null;
  meId: string;
};

function Row({
  row: r,
  grid,
  handledBy,
  aeKind,
  now,
  rule,
  role,
  page,
  tab,
  open,
  checkbox,
  onOpen,
  onReassign,
  onHand,
  onAddNote,
  onAccept,
  onSendBack,
  onPass,
  onTakeOver,
  meId,
}: RowProps) {
  const status = statuses[r.status];
  const upcoming = band(r.daysLeft) === 'upcoming' && stageOf(r) === 'open';
  const diff = change(r, rule);
  const reason = aeKind ? rowReason(r, aeKind === 'finished') : null;
  const act = aeKind && r.handoff ? actByChip(r.handoff, now) : null;
  const follow = aeKind === 'withme' ? followUpText(r.handoff?.followUpOn, now) : null;
  return (
    // The whole row opens the Quick view (spec C7.3); the name is the keyboard target.
    <div
      className={open || checkbox?.on ? 'trow sel' : 'trow'}
      style={{ gridTemplateColumns: grid }}
      onClick={onOpen}
    >
      {checkbox && (
        <div className="td selc" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="cbx"
            aria-label={`Select ${r.insured}`}
            checked={checkbox.on}
            onChange={checkbox.pick}
          />
        </div>
      )}
      <div className="td ink inscell g8">
        {/* The name shrinks to an ellipsis so the note icon keeps its place (the kit's Insured cell). */}
        <Tip text={r.insuredFull} style={{ minWidth: 0, flex: '0 1 auto' }}>
          <button
            type="button"
            className="ins"
            aria-haspopup="dialog"
            aria-label={`Quick view ${r.insured}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            {r.insured}
          </button>
        </Tip>
        {r.hasNote && (
          <Tip text="Has a note" focusable>
            <NoteIcon className="ico flagi" />
          </Tip>
        )}
      </div>
      {aeKind && (
        <div className="td vis">{reason && <span className={reason.cls}>{reason.label}</span>}</div>
      )}
      {handledBy && !aeKind && (
        <div className="td text2">
          <span className="ell">{r.handledBy?.name ?? 'Unassigned'}</span>
        </div>
      )}
      <div className="td mono">
        <span className="ell">{r.policyNumber}</span>
      </div>
      <div className="td">
        <span className="ell">{shortLine(r.line)}</span>
      </div>
      <div className="td vis">
        <Tip text={expiresTip(r)} focusable>
          <span className={daysPill(r.daysLeft)}>{daysLabel(r.daysLeft)}</span>
        </Tip>
      </div>
      {aeKind === 'ask' || aeKind === 'withme' ? (
        // Act by (amber within 3 days, red once overdue); on With me also the Follow up day. A take
        // over (reshop, Assign to me) has no Act by date.
        <div className="td vis g6">
          {act && (
            <Tip text={act.tip} focusable>
              <span className={act.cls}>{act.text}</span>
            </Tip>
          )}
          {follow && <span className="tag t-blue">{follow}</span>}
          {!act && !follow && <span className="faint">-</span>}
        </div>
      ) : aeKind === 'finished' ? (
        <div className="td">
          <span className="ell">
            {r.finishedHandoff ? resultText(r.finishedHandoff, meId) : ''}
          </span>
        </div>
      ) : (
        <>
          <div className="td mono">
            <span className="ell">{money(r.priorPremium)}</span>
          </div>
          <div className="td mono ink">
            <span className="ell">{money(r.renewalPremium)}</span>
          </div>
          <div className="td mono">{diff && <span className={diff.cls}>{diff.text}</span>}</div>
        </>
      )}
      {(aeKind === 'ask' || aeKind === 'finished') && (
        <div className="td">
          <span className="ell">{fromName(r, aeKind === 'finished')}</span>
        </div>
      )}
      {aeKind === 'ask' && (
        <div className="td">
          <span className="ell">{r.handoff ? sentText(r.handoff, now) : ''}</span>
        </div>
      )}
      {(!aeKind || aeKind === 'withme') && (
        <div className="td vis stc">
          <span className={`pill ${status.pill}`}>{upcoming ? 'Not started' : status.label}</span>
          <AwaitingTag h={r.handoff} meId={meId} />
          {/* On a branch (spec C4, S17): yellow Non-renewal with its date, grey Policy inactive. */}
          {r.branch && (
            <Tip text={branchTag(r.branch).tip} focusable>
              <span className={branchTag(r.branch).className}>{branchTag(r.branch).text}</span>
            </Tip>
          )}
          {showIncreaseTag(r, rule) && (
            <Tip text={increaseTooltip(r, rule)} focusable>
              <span className="tag t-amber">Increase</span>
            </Tip>
          )}
          {upcoming && <span className="tag t-grey">Not in a queue yet</span>}
          {/* Step 2 waits for AMS360's download and a recheck is set (S16 Batch 3). */}
          {r.recheck && <span className="tag t-amber">{recheckTag(r.recheck)}</span>}
          {r.fetch.running && (
            <Tip text={fetchingTip(r.fetch.waiting)} focusable>
              <span className="tag t-blue">
                <span className="spin" />
                Fetching
              </span>
            </Tip>
          )}
          {r.renewedInAmsAt && (
            <Tip text={renewedTip(r.renewedInAmsAt, now)} focusable>
              {/* Short in the row (Yehuda, Oct 25); the full words and the time are in the tooltip. */}
              <span className="tag t-blue">
                <CheckIcon className="ico s12" />
                AMS
              </span>
            </Tip>
          )}
          {r.inactiveInAmsSince && (
            <Tip
              text={`No longer active in AMS360 since ${when(r.inactiveInAmsSince, now)}.`}
              focusable
            >
              <span className="tag t-grey">Inactive</span>
            </Tip>
          )}
        </div>
      )}
      <div className="td vis racts">
        {/* Accept on the row: shown on hover, always when overdue (the Renewals board). */}
        {onAccept && (
          <button
            type="button"
            className={isOverdue(r.handoff, now) ? 'btn btn-sm accb on' : 'btn btn-sm accb'}
            aria-label={`Accept ${r.insured}`}
            onClick={(e) => {
              e.stopPropagation();
              onAccept();
            }}
          >
            <CheckIcon className="ico s12" />
            Accept
          </button>
        )}
        <Link
          className="openb"
          to={`/records/${r.id}`}
          state={{ from: page, tab }}
          aria-label={`Open record for ${r.insured}`}
          onClick={(e) => e.stopPropagation()}
        >
          Open record
        </Link>
        <RowMenu
          row={r}
          role={role}
          page={page}
          tab={tab}
          meId={meId}
          onQuickView={onOpen}
          onReassign={onReassign}
          onHand={onHand}
          onAddNote={onAddNote}
          onSendBack={onSendBack}
          onPass={onPass}
          onTakeOver={onTakeOver}
        />
      </div>
    </div>
  );
}
